/* eslint-disable no-shadow */
/* eslint-disable camelcase */
import React, { FC, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import qs from 'qs';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import { getPageContent, getSelectedInfo, getUpdatedParams } from '../utils/commonRingUtils';
import { getPageData } from '../utils/Pages';
import URLs from '../utils/url';
import { ITab } from '../types/customizer';
import DesktopSidebar from '../components/Sidebar/DesktopSidebar';
import MobileSidebar from '../components/Sidebar/MobileSidebar';
import './index.scss';
import If from '../components/If';
import undoIcon from '../assets/images/Undo-Arrow.svg';
import MyGallery from '../components/MyGallery';
import AppTabs from '../components/AppTabs';
import Loading from '../components/Shared/Loader';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { RootState } from '../redux/store';
import {
  getProductDetail,
  createProduct,
  getScreensaverData,
  getStoreDataAction,
  getBestSellerProduct
} from '../redux/actions/customizer';
import { resetCreatedProduct } from '../redux/slices/customizer/customizerSlice';
import RingCustomizerHOC, { ICustomizerHOCProps, IParams, IUrl } from '../components/RingCustomizerHOC';
import { APP_STYLES, isDoneAtBottom, urlToParams } from '../utils/basic';
import { compareParams } from '../utils/helper';
import Banner from '../components/Banner';
import ReverseIcon from '../assets/images/contrast.png';
import AppModal from '../components/AppModal';
import DiscountedPopup from '../components/DiscountedPopup';
import axiosClient from '../redux/services/apiClient';
import LashbrookLogo from '../assets/images/Lashbrook_Logo.svg';
import { Tooltip } from 'reactstrap';
import BuilderFavourites from '../components/BuilderFavourites';
import { addFavourite, deleteFavourite, getFavourites } from '../redux/actions/customizer';
import { IDeleteFavoriteRequestPayload } from '../redux/types/builder';
import { isFavouriteUrlMatch, normalizeFavouriteUrl } from '../utils/favourites';
import { sendAnalytics } from '../utils/analytics';
import settings from '../config/settings';
import { bsLog, isBestSellerLoading } from '../utils/bestSeller';

// Screensaver configuration defaults
const SCREENSAVER_CONFIG = {
  // Enable screensaver for all users (true) or only logged-in users (false)
  ENABLE_FOR_ALL_USERS: true,
  // Fade transition duration in milliseconds
  FADE_DURATION: 500,
  // How many seconds before the screensaver appears to preload carousel ring prices
  PRICE_PRELOAD_LEAD_SECONDS: 10
};

import SCREENSAVER_RINGS from './screensaverRings';

/**
 * Returns true only for desktop touchscreen devices.
 * Excludes mobile and tablet by checking screen width and user agent.
 */
const isDesktopTouchscreen = (): boolean => {
  const hasTouchscreen = navigator.maxTouchPoints > 0;
  if (!hasTouchscreen) return false;

  const isLargeScreen = window.matchMedia('(min-width: 1024px)').matches;
  if (!isLargeScreen) return false;

  // Exclude mobile/tablet user agents even if they have a large viewport
  const ua = navigator.userAgent.toLowerCase();
  const isMobileOrTablet = /android|iphone|ipad|ipod|mobile|tablet/.test(ua);

  return !isMobileOrTablet;
};

export interface ICustomizerProps {
  match: any;
  location: any;
  history: any;
  type: number;
  params: IParams;
  setParams: (params: IParams) => void;
  url: IUrl;
  isQueryHandling?: boolean;
}

const RingCustomizer = ({ location, history, type, params, url, isQueryHandling, setParams }: ICustomizerProps) => {
  const [pages, setPages] = useState<ITab[]>([]);
  const [key, setKey] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [myHistory, setMyHistory] = useState<string[]>([]);
  const [discountOpen, setDiscountOpen] = useState<boolean>(false);
  const [proceedInfoOpen, setProceedInfoOpen] = useState<boolean>(false);
  // Distinguishes which flow triggered createProduct: "Proceed As Is" shows the
  // confirmation modal, while the main "Done" button navigates to the PDP.
  const showProceedInfoRef = useRef(false);
  const [proceedInfo, setProceedInfo] = useState<{
    description?: string;
    price: string;
    size?: number;
    sku: string;
    referenceCode: string;
    engravingFont?: string;
    engravingText?: string;
  } | null>(null);
  const [engraving, setEngravingState] = useState('');
  // Mirror the engraving into a ref so `handleBestSellerDone` always reads the
  // latest value. The engraving is committed on the input's blur, which fires on
  // the button's mousedown — before the click handler runs — but a state read in
  // that handler would still see the stale render closure. The ref updates
  // synchronously, so it is current by click time.
  const engravingRef = useRef('');
  const setEngraving = (text: string) => {
    engravingRef.current = text;
    setEngravingState(text);
  };
  const [inactivityModalOpen, setInactivityModalOpen] = useState<boolean>(false);
  const [isFading, setIsFading] = useState<boolean>(false);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pricePreloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const screensaverPricesFetchedRef = useRef<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const screensaverFetchedRef = useRef<boolean>(false);
  const storeDataFetchedRef = useRef<boolean>(false);
  const [ringPrices, setRingPrices] = useState<Record<number, string>>({});
  const [carouselPage, setCarouselPage] = useState(0);
  const carouselTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const carouselPausedUntilRef = useRef<number>(0);
  const bestSellerHandledRef = useRef<string | null>(null);

  useEffect(() => {
    if (params.otherQueryParams?.bestSeller) {
      setDiscountOpen(true);
    }
  }, [params.otherQueryParams?.bestSeller]);

  const dispatch = useAppDispatch();
  const {
    product,
    isFetching,
    createdProduct,
    bestSellerProduct: bProduct,
    bestSellerPendingId,
    selectedShippingPriority,
    screensaverData,
    storeData,
    favourites
  } = useAppSelector((state: RootState) => ({ ...state.customizer }));
  const initialParamsRef = useRef<IParams>(params);
  const favoritesEnabled = useMemo(() => settings.getSettings().allowFavorites === true, []);
  const [showFavourites, setShowFavourites] = useState<boolean>(false);
  const [favTooltipOpen, setFavTooltipOpen] = useState(false);
  const isFavorited = useMemo(() => {
    if (!product?.ring_options_url) return false;
    return favourites.some((fav) => isFavouriteUrlMatch(fav.product_url, product.ring_options_url));
  }, [favourites, product?.ring_options_url]);

  useEffect(() => {
    if (favoritesEnabled) dispatch(getFavourites());
  }, [favoritesEnabled]);

  useEffect(() => {
    if (showFavourites && favourites.length === 0) {
      setShowFavourites(false);
    }
  }, [favourites, showFavourites]);

  // `can_select_shipping_priority` from admin/v1/builder-platform/status marks a
  // store-facing build. It drives the Ready to Ship modal's proceed button: in a
  // store the click ends at the confirmation modal with the sku and reference
  // code, otherwise it purchases like the builder's "Done" button.
  const canSelectShippingPriority = Boolean(storeData?.can_select_shipping_priority);

  // Memoize the video URL so it doesn't change on re-renders
  const screensaverVideoUrl = useMemo(() => {
    if (!screensaverData?.video_path) return '';
    const separator = screensaverData.video_path.indexOf('?') !== -1 ? '&' : '?';
    return `${screensaverData.video_path}${separator}rand=${Math.random().toString(12)}`;
  }, [screensaverData?.video_path]);

  useEffect(() => {
    // Fetch screensaver data once on mount
    if (!screensaverFetchedRef.current) {
      screensaverFetchedRef.current = true;
      dispatch(getScreensaverData());
    }
    if (!storeDataFetchedRef.current) {
      storeDataFetchedRef.current = true;
      dispatch(getStoreDataAction());
    }
  }, [dispatch]);

  // Lazily fetch prices for screensaver carousel rings. Runs at most once, and is
  // scheduled to fire shortly before the screensaver appears (see resetInactivityTimer)
  // so we don't make these extra calls on every builder page load.
  const preloadScreensaverPrices = useCallback(() => {
    if (screensaverPricesFetchedRef.current || SCREENSAVER_RINGS.length === 0) return;
    screensaverPricesFetchedRef.current = true;
    SCREENSAVER_RINGS.forEach((ring, index) => {
      // Strip /helzberg prefix and prepend /v1
      const apiPath = ring.ringUrl.replace(/^\/helzberg/, '');
      axiosClient
        .get(`/v1${apiPath}`)
        .then((res) => {
          if (res.data?.price) {
            setRingPrices((prev) => ({ ...prev, [index]: res.data.price }));
          }
        })
        .catch(() => {
          // Silently ignore price fetch failures
        });
    });
  }, []);

  useEffect(() => {
    if (!url.ring_url) return;
    const bestSellerId = params.otherQueryParams?.bestSeller;
    if (bestSellerId) {
      if (bestSellerHandledRef.current !== String(bestSellerId)) return;
      if (url.ring_url === bProduct?.ring_options_url) return;
    }
    dispatch(getProductDetail({ url: url.ring_url, type }));
  }, [url.ring_url, params.otherQueryParams?.bestSeller]);

  useEffect(() => {
    const bestSellerId = params.otherQueryParams?.bestSeller;
    if (!bestSellerId) return;
    if (!bProduct || String(bProduct.bestseller_id ?? '') !== String(bestSellerId)) return;
    if (!bProduct.ring_options_url) return;
    if (bestSellerHandledRef.current === String(bestSellerId)) return;
    bestSellerHandledRef.current = String(bestSellerId);
    bsLog('loading builder for best seller', { bestSellerId, ringUrl: bProduct.ring_options_url });
    dispatch(getProductDetail({ url: bProduct.ring_options_url, type }));
    if (url.ring_url !== bProduct.ring_options_url) {
      navigateToUrl(bProduct.ring_options_url, true);
    }
  }, [bProduct, params.otherQueryParams?.bestSeller]);

  useEffect(() => {
    // Clean up inactivity timer on unmount
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (pricePreloadTimerRef.current) {
        clearTimeout(pricePreloadTimerRef.current);
      }
    };
  }, []);

  // Toggle body class to hide third-party overlays during screensaver
  useEffect(() => {
    if (inactivityModalOpen) {
      document.body.classList.add('screensaver-active');
    } else {
      document.body.classList.remove('screensaver-active');
    }
    return () => {
      document.body.classList.remove('screensaver-active');
    };
  }, [inactivityModalOpen]);

  // Auto-cycle carousel pages
  useEffect(() => {
    const totalPages = Math.ceil(SCREENSAVER_RINGS.length / 4);
    if (totalPages <= 1 || !inactivityModalOpen) {
      if (carouselTimerRef.current) clearInterval(carouselTimerRef.current);
      return;
    }
    carouselTimerRef.current = setInterval(() => {
      if (Date.now() < carouselPausedUntilRef.current) return;
      setCarouselPage((prev) => (prev + 1) % totalPages);
    }, 10000);
    return () => {
      if (carouselTimerRef.current) clearInterval(carouselTimerRef.current);
    };
  }, [inactivityModalOpen]);

  useEffect(() => {
    if (!product) {
      return;
    }
    if (url.ring_url && product.ring_options_url !== url.ring_url) {
      navigateToUrl(product.ring_options_url, true);
    }

    // const tabData = getPageData(product, params.pathParams.pathPrefix === 'helzberg');
    const tabData = getPageData({
      product,
      type,
      subpath: '',
      ring_type: params.pathParams.ring_type,
      storeData,
      isBuildFromScratch: params?.queryParams?.isBuildFromScratch === 'true',
      showCollegiateBands: storeData?.show_collegiate_bands_builder
    });
    const activekey = getActiveTab(tabData);

    setPages(tabData);
    if (!key) {
      setKey(activekey);
    }
  }, [product]);

  useEffect(() => {
    if (createdProduct) {
      dispatch(resetCreatedProduct());
      if (showProceedInfoRef.current) {
        // "Proceed As Is": store-facing confirmation modal with the saved
        // product's price, sku and reference code (no PDP navigation).
        showProceedInfoRef.current = false;
        setDiscountOpen(false);
        setProceedInfoOpen(true);
      } else {
        // "Done": navigate to the product page as before.
        const productUrl = `${URLs.CUSTOM_PDP}?pid=${createdProduct.ring_category_sku}&lbuuid=${createdProduct.id}`;
        window.open(productUrl, '_self');
      }
    }
  }, [createdProduct]);

  useEffect(() => {
    const { pathParams: initialPathParams, queryParams: initialQueryParams } = initialParamsRef.current;
    if (Object.keys(initialPathParams).length === 0 && Object.keys(initialQueryParams).length === 0) {
      initialParamsRef.current = { pathParams: { ...params.pathParams }, queryParams: { ...params.queryParams } };
    }
  }, [params]);

  useEffect(() => {
    if (params.otherQueryParams?.base_ring_url) {
      const baseUrl = params.otherQueryParams.base_ring_url;
      initialParamsRef.current = urlToParams(baseUrl);
    }
  }, [params.otherQueryParams?.base_ring_url]);

  const navigateToUrl = (url: string, replace = true) => {
    let navigateUrl = url; // In future if you want add any path to ring url
    /**
     * In ring url, Attach some path and query params
     * If there is pathPrefix, attach before url
     * If there is queryHandling, attach entire url as queryParam (ring_url)
     * If there is otherQueryString, attach end of url
     */
    const {
      pathParams: { pathPrefix }
    } = params;
    if (pathPrefix) {
      navigateUrl = `/${pathPrefix}${navigateUrl}`;
    }

    if (isQueryHandling) {
      navigateUrl = `${location.pathname}?ring_url=${encodeURIComponent(navigateUrl)}`;
    }
    const otherQueryString = new URLSearchParams(params.otherQueryParams).toString();
    if (otherQueryString) {
      navigateUrl = `${navigateUrl}&${otherQueryString}`;
    }

    addHistory(navigateUrl, replace);
    if (replace) {
      history.replace(navigateUrl); // After url is navigated it calls a useffect of location dependencies
    } else {
      history.push(navigateUrl); // After url is navigated it calls a useffect of location dependencies
    }
  };

  const handleToggleFavourite = () => {
    if (!product) return;
    const pathPrefix = params.pathParams.pathPrefix;
    if (isFavorited) {
      sendAnalytics('favorites', 'Remove Favorite - Builder', pathPrefix);
      const matchingFav = favourites.find((fav) => isFavouriteUrlMatch(fav.product_url, product.ring_options_url));
      const data: IDeleteFavoriteRequestPayload = { product_url: matchingFav?.product_url || product.ring_options_url };
      dispatch(deleteFavourite(data)).then(() => dispatch(getFavourites()));
    } else {
      sendAnalytics('favorites', 'Add Favorite - Builder', pathPrefix);
      const data = {
        product_url: normalizeFavouriteUrl(product.ring_options_url),
        ring_image_url: product.images?.[0] || ''
      };
      dispatch(addFavourite(data)).then(() => dispatch(getFavourites()));
    }
  };

  const handleFavouriteNavigate = (navUrl: string) => {
    setShowFavourites(false);
    navigateToUrl(navUrl);
  };

  const handleUrlChange = ({ pathParams, queryParams }: IParams) => {
    let URL = '';
    if (type === 1) {
      const { profile, width, ring_materials } = pathParams;
      const searchString = qs.stringify(queryParams);
      URL = `/rings/profiles/${profile}/widths/${width}/ring_materials/${ring_materials}?${searchString}`;
    } else if (type === 2) {
      const { styles, ring_materials, color_arrangements, stone_quantities, variants } = pathParams;
      const searchString = qs.stringify(queryParams);
      URL = `/women/rings/styles/${styles}/ring_materials/${ring_materials}/color_arrangements/${color_arrangements}/stone_quantities/${stone_quantities}/variants/${variants}?${searchString}`;
    } else if (type === 3) {
      const { ring_type, ring_materials } = pathParams;
      const searchString = qs.stringify(queryParams);
      URL = `/rings/${ring_type}/ring_materials/${ring_materials}?${searchString}`;
    } else if (type === 4) {
      const { ring_type, profile, width, ring_materials } = pathParams;
      const searchString = qs.stringify(queryParams);
      URL = `/rings/${ring_type}/profiles/${profile}/widths/${width}/ring_materials/${ring_materials}?${searchString}`;
    }
    navigateToUrl(URL, false); // push new URL
  };

  const handleUpdate = (tobeUpdated: any) => {
    const updatedParams = getUpdatedParams(tobeUpdated, params);
    handleUrlChange(updatedParams);
  };

  const getActiveTab = (items: ITab[]) => {
    const selectedTab = items.find((tab) => tab.isSelected);
    let key = '';
    if (selectedTab) {
      key = selectedTab.eventKey;
    } else {
      key = items?.[0]?.eventKey || '';
    }
    return key;
  };

  const toggleOpen = () => {
    setOpen(!open);
  };

  const handleActiveTab = (key: string, toggleVisibility: boolean) => () => {
    setKey(key);
    setShowFavourites(false);
    if (toggleVisibility) {
      toggleOpen();
    }
  };

  const addHistory = (url: string, replace: boolean) => {
    setMyHistory([url]);
  };

  const removeHistory = () => {
    setMyHistory([]);
  };

  const handleUndo = () => {
    if (myHistory.length) {
      history.goBack();
      removeHistory();
    }
  };

  const handleDone = async () => {
    const paramsChanged = compareParams(initialParamsRef.current, params);
    const length = paramsChanged.length;
    let changes_made;
    /*
    if (length === 0) {
      if (params.otherQueryParams?.product_id) {
        if (params.otherQueryParams?.redirect_url) {
          window.location.href = params.otherQueryParams.redirect_url;
        } else {
          toast.info('No changes made');
        }
        return;
      } else {
        changes_made = 'full_customization';
      }
    } else {
    }
    */
    if (length === 0) {
      changes_made = 'no_changes';
    } else if (length === 1 && paramsChanged.includes('size')) {
      changes_made = 'only_size';
    } else if (paramsChanged.includes('engraving_new') && params.queryParams.engraving_new === 'T') {
      toast.warn('Select any font style and Please enter text');
      return;
    } else {
      changes_made = 'full_customization';
    }

    let ring_url = product.ring_options_url;
    if (params.pathParams.pathPrefix) {
      ring_url = `/${params.pathParams.pathPrefix}${ring_url}`;
    }
    const data = {
      ring_url: ring_url,
      base_ring_url: params.otherQueryParams?.base_ring_url || null,
      ring_images: product.images,
      ring_description: product.description,
      ring_sku: product.sku,
      price: product.price,
      tcw: product.tcw,
      title: product.title,
      ring_category_sku: product.helzberg_custom_sku,
      magic_number: product.magic_number,
      wholesale_price: product.wholesale_price,
      changes_made,
      client_name: params.pathParams.pathPrefix,
      custom_sku: params.otherQueryParams?.product_id || null,
      ring_options: getPageContent(pages, 'payload')
    };
    showProceedInfoRef.current = false;
    dispatch(createProduct({ data, selectedShippingPriority }));
  };

  const handleBestSellerDone = async (size?: number) => {
    const paramsChanged = compareParams(initialParamsRef.current, params);
    const length = paramsChanged.length;
    let changes_made;
    if (length === 0) {
      changes_made = 'no_changes';
    } else if (length === 1 && paramsChanged.includes('size')) {
      changes_made = 'only_size';
    } else if (paramsChanged.includes('engraving_new') && params.queryParams.engraving_new === 'T') {
      toast.warn('Select any font style and Please enter text');
      return;
    } else {
      changes_made = 'full_customization';
    }

    // The engraving-aware magic number is produced by re-fetching the best-seller
    // product with the current size + engraving. That refetch is normally triggered
    // on the engraving input's blur, which may not have resolved yet when the user
    // clicks "Proceed As Is" directly. Re-fetch and await it here so the saved
    // product and the confirmation modal use the correct magic number.
    const engraving = engravingRef.current;
    let freshProduct = bProduct;
    try {
      freshProduct = await dispatch(
        getBestSellerProduct({
          id: bProduct?.bestseller_id,
          data: { size: size ?? '', engraving }
        })
      ).unwrap();
    } catch (error) {
      console.log(error);
    }

    let ring_url = freshProduct?.ring_options_url;
    if (params.pathParams.pathPrefix) {
      ring_url = `/${params.pathParams.pathPrefix}${ring_url}`;
    }

    const pageData = getPageData({
      product: freshProduct,
      type,
      subpath: '',
      ring_type: params.pathParams.ring_type,
      storeData: { show_women_builder: false, show_signet_builder: true, show_collegiate_builder: true },
      isBuildFromScratch: false
    });

    const data = {
      bestseller_id: freshProduct?.bestseller_id,
      msrp: freshProduct?.msrp,
      engraving: engraving,

      ring_url: ring_url,
      base_ring_url: params.otherQueryParams?.base_ring_url || null,
      ring_images: freshProduct?.images,
      ring_description: freshProduct?.description,
      ring_sku: freshProduct?.sku,
      price: freshProduct?.price,
      tcw: freshProduct?.tcw,
      title: freshProduct?.title,
      ring_category_sku: freshProduct?.helzberg_custom_sku,
      magic_number: freshProduct?.magic_number,
      wholesale_price: freshProduct?.wholesale_price,
      changes_made,
      client_name: params.pathParams.pathPrefix,
      custom_sku: params.otherQueryParams?.product_id || null,
      ring_options: getPageContent(pageData, 'payload')
    };
    console.log(data);
    // Snapshot the values to show in the confirmation modal once the product saves.
    // The engraving is stored as "I_<sku>-<fontName>_<text>" (empty when there is
    // no engraving). Split off the font name and the text for display.
    let engravingFont: string | undefined;
    let engravingText: string | undefined;
    if (engraving) {
      const rest = engraving.startsWith('I_') ? engraving.slice(2) : engraving;
      const sepIndex = rest.indexOf('_');
      if (sepIndex !== -1) {
        const fontPart = rest.slice(0, sepIndex);
        const dashIndex = fontPart.indexOf('-');
        engravingFont = dashIndex !== -1 ? fontPart.slice(dashIndex + 1) : fontPart;
        engravingText = rest.slice(sepIndex + 1);
      }
    }
    setProceedInfo({
      description: freshProduct?.description,
      price: freshProduct?.price,
      size,
      sku: freshProduct?.helzberg_custom_sku,
      referenceCode: freshProduct?.magic_number,
      engravingFont,
      engravingText
    });
    // In a store the click ends at the confirmation modal; otherwise it is a
    // purchase and falls through to the same PDP navigation the builder's "Done"
    // button performs.
    //
    // The flag is re-read here rather than taken from the value fetched on mount:
    // an in-store machine can sit on one page for days, and toggling the store's
    // settings has to take effect without someone reloading the browser.
    let isStoreBuild = canSelectShippingPriority;
    try {
      const freshStoreData = await dispatch(getStoreDataAction()).unwrap();
      isStoreBuild = Boolean(freshStoreData?.can_select_shipping_priority);
    } catch (error) {
      // Endpoint unreachable — keep whatever the last successful fetch reported.
      console.log(error);
    }
    bsLog('proceed as is', { isStoreBuild, mountValue: canSelectShippingPriority });
    showProceedInfoRef.current = isStoreBuild;
    await dispatch(createProduct({ data, selectedShippingPriority }));
  };

  /**
   * Carry the size + engraving selected in the discount flow over to the builder,
   * so the user picks up where they left off. Engraving is read from the ref so it
   * is current even when triggered without blurring the engraving input first.
   */
  const applySelectionsToBuilder = (fingerSizeSku?: string) => {
    const updates: Record<string, string> = {};
    if (fingerSizeSku) {
      // The size param key differs by builder version: type 3 (v3) uses
      // `finger_size`; types 1 & 2 (v1/v2) use `size`.
      const sizeParamKey = type === 3 ? 'finger_size' : 'size';
      updates[sizeParamKey] = fingerSizeSku;
    }
    const engraving = engravingRef.current;
    if (engraving) {
      updates.engraving_new = engraving;
    }
    if (Object.keys(updates).length) {
      handleUpdate(updates);
    }
  };

  // Map a numeric finger size back to its option sku (the value the builder wants).
  const getSizeSku = (value?: number): string | undefined => {
    if (value == null) return undefined;
    const sizeOptions: any[] = bProduct?.finger_size || bProduct?.size || [];
    const match = sizeOptions.find((opt) => Math.abs(parseFloat(opt.sku) - value) < 0.001);
    return match?.sku;
  };

  // "Customize in Lashbrook Builder" button.
  const handleCustomize = (fingerSizeSku?: string) => {
    applySelectionsToBuilder(fingerSizeSku);
    setDiscountOpen(false);
  };

  const pathPrefix = params.pathParams.pathPrefix;

  const handleEngravingReversePattern = () => {
    const { selectedOption } = engravingInfo;
    if (selectedOption.is_reverse_selected) {
      handleUpdate({ engraving_pattern: selectedOption.sku });
    } else {
      handleUpdate({ engraving_pattern: `${selectedOption.sku}REV` });
    }
  };

  const handleLogoReversePattern = () => {
    const { selectedOption } = SignetEngravingInfo;
    if (selectedOption.is_reverse_selected) {
      handleUpdate({ signet_engraving: selectedOption.sku });
    } else {
      handleUpdate({ signet_engraving: `${selectedOption.sku}REV` });
    }
  };

  // Switching best seller variants (the weight buttons in the Ready to Ship
  // modal). The selection is applied to `params` right away so the modal starts
  // loading the new variant, and mirrored into the url because `params` is rebuilt
  // from the url on every location change — a selection kept only in local state
  // gets reverted to the previous best seller by the navigation that follows.
  const handleBestSeller = (id: string) => {
    bsLog('variant clicked', { from: params.otherQueryParams?.bestSeller, to: id });
    setParams({ ...params, otherQueryParams: { ...params.otherQueryParams, bestSeller: id } });
    const search = new URLSearchParams(location.search);
    search.set('bestSeller', String(id));
    history.replace(`${location.pathname}?${search.toString()}`);
  };

  const resetInactivityTimer = useCallback(() => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Don't start timer if screensaver is already open
    if (inactivityModalOpen) {
      return;
    }

    // Check if screensaver should be enabled
    const shouldEnable = SCREENSAVER_CONFIG.ENABLE_FOR_ALL_USERS;
    if (!shouldEnable) {
      return;
    }

    // Only enable screensaver on desktop touchscreen devices
    if (!isDesktopTouchscreen()) {
      return;
    }

    // Only enable screensaver if API data indicates it's active and has a video
    if (!screensaverData || !screensaverData.active || !screensaverData.video_path) {
      return;
    }

    const timeoutSeconds = screensaverData.time_in_seconds;

    // Preload the carousel ring prices a few seconds before the screensaver appears,
    // so prices are ready when it opens without fetching on every page load.
    if (pricePreloadTimerRef.current) {
      clearTimeout(pricePreloadTimerRef.current);
    }
    if (!screensaverPricesFetchedRef.current) {
      const leadMs = (timeoutSeconds - SCREENSAVER_CONFIG.PRICE_PRELOAD_LEAD_SECONDS) * 1000;
      pricePreloadTimerRef.current = setTimeout(preloadScreensaverPrices, Math.max(0, leadMs));
    }

    // Set new timer (convert seconds to milliseconds)
    inactivityTimerRef.current = setTimeout(() => {
      setIsFading(true);
      setTimeout(() => {
        setInactivityModalOpen(true);
        setIsFading(false);
        trackScreensaverClick('Screensaver Launch');
        // Ensure video plays when screensaver opens
        if (videoRef.current) {
          videoRef.current.play().catch(console.error);
        }
      }, SCREENSAVER_CONFIG.FADE_DURATION);
    }, timeoutSeconds * 1000);
  }, [inactivityModalOpen, screensaverData, preloadScreensaverPrices]);

  const trackScreensaverClick = useCallback((target: string | null) => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const today = `${yyyy}-${mm}-${dd}`;
    fetch('https://services.lashbrookdesigns.com/screensaver_analytics.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date: today, target, builder: 'Helzberg' })
    }).catch(() => {});
  }, []);

  const closeInactivityModal = useCallback(() => {
    setIsFading(true);
    setTimeout(() => {
      setInactivityModalOpen(false);
      setIsFading(false);
      resetInactivityTimer();
    }, SCREENSAVER_CONFIG.FADE_DURATION);
  }, [resetInactivityTimer]);

  /**
   * Set up activity listeners
   */
  useEffect(() => {
    // Start initial timer
    resetInactivityTimer();

    // Resets the inactivity timer on any activity
    const handleActivity = () => {
      if (!inactivityModalOpen) {
        resetInactivityTimer();
      }
    };

    // Dismisses the screensaver only on click/touch
    const handleDismiss = () => {
      if (inactivityModalOpen) {
        closeInactivityModal();
      } else {
        resetInactivityTimer();
      }
    };

    // Dismisses the screensaver when returning to this tab from another tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (inactivityModalOpen) {
          closeInactivityModal();
        } else {
          resetInactivityTimer();
        }
      }
    };

    // Non-dismissing activity listeners (reset timer only)
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keypress', handleActivity);
    window.addEventListener('scroll', handleActivity);

    // Dismissing listeners (close screensaver on click/touch)
    window.addEventListener('mousedown', handleDismiss);
    window.addEventListener('touchstart', handleDismiss);
    window.addEventListener('click', handleDismiss);

    // Dismiss when the tab regains visibility
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keypress', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('mousedown', handleDismiss);
      window.removeEventListener('touchstart', handleDismiss);
      window.removeEventListener('click', handleDismiss);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (pricePreloadTimerRef.current) {
        clearTimeout(pricePreloadTimerRef.current);
      }
    };
  }, [inactivityModalOpen, resetInactivityTimer, closeInactivityModal]);

  const selectedPage = pages.find((item) => item.eventKey === key);
  const engravingInfo = key === 'engraving' ? getSelectedInfo(selectedPage) : null;
  const SignetEngravingInfo = key === 'logo' || key === 'outsideFeature_1' ? getSelectedInfo(selectedPage) : null;

  return (
    <>
      <Helmet>
        <link
          rel="icon"
          href={
            pathPrefix ? `${process.env.PUBLIC_URL}/${pathPrefix}-favicon.ico` : `${process.env.PUBLIC_URL}/favicon.ico`
          }
        />
        <title>Ring builder</title>
      </Helmet>
      <Banner pathPrefix={pathPrefix} />
      <If condition={!isDoneAtBottom(pathPrefix || '')}>
        <div className="tw-flex tw-items-center tw-justify-center tw-px-3 tw-pt-3 tw-w-full tw-top-0 tw-bg-white lg:tw-hidden">
          <button
            className="tw-bg-brand-color tw-text-tertiary-color tw-text-base tw-px-9 tw-py-2 tw-rounded-tertiary tw-normal-case tw-font-[inherit]"
            onClick={handleDone}
          >
            Done - {product?.price}
          </button>
        </div>
      </If>
      <div className="secondary-font-family customizer_main_container tw-flex-1 tw-flex">
        <DesktopSidebar
          pages={pages}
          selectedTab={showFavourites ? '' : key}
          handleActiveTab={handleActiveTab}
          handleDone={handleDone}
          price={product?.price}
          pathPrefix={pathPrefix}
          favouritesCount={favoritesEnabled ? favourites.length : 0}
          showFavourites={showFavourites}
          onFavouritesClick={
            favoritesEnabled
              ? () => {
                  sendAnalytics('favorites', 'View Favorites - Builder', pathPrefix);
                  setShowFavourites(true);
                }
              : undefined
          }
        />
        <div className="tw-flex-1 tw-w-0 tw-flex tw-flex-col">
          {' '}
          {/* tw-w-0 is most important it will set overflow width to hidden. */}
          <div
            className={clsx('gallery_container tw-relative tw-pb-5 tw-pt-5 lg:tw-pt-10', {
              'lg:tw-hidden': !showFavourites && ['summaryInfo', 'summary', 'data'].includes(key)
            })}
          >
            <MyGallery images={product?.images || []} />
            {favoritesEnabled && product && (
              <div className="tw-absolute tw-bottom-1 tw-right-3 sm:tw-right-6 lg:tw-right-20 tw-z-10 secondary-font-family">
                <div className="builder-fav-toggle">
                  <button
                    id="builder-fav-btn"
                    type="button"
                    className="builder-add-fav tw-outline-none"
                    onClick={handleToggleFavourite}
                    aria-label={isFavorited ? 'Remove Design' : 'Save Design'}
                  >
                    <i
                      className={`fa ${isFavorited ? 'fa-bookmark' : 'fa-bookmark-o'} builder-fav-toggle__icon`}
                      aria-hidden
                    />
                    {favourites.length > 0 && <span className="builder-fav-toggle__badge">{favourites.length}</span>}
                  </button>
                  <Tooltip
                    placement="top"
                    isOpen={favTooltipOpen}
                    target="builder-fav-btn"
                    toggle={() => setFavTooltipOpen(!favTooltipOpen)}
                    className="fav-tooltip"
                  >
                    {isFavorited ? 'Remove Design' : 'Save Design'}
                  </Tooltip>
                  {favourites.length > 0 && (
                    <button
                      type="button"
                      className="builder-fav-toggle__label-btn tw-outline-none"
                      onClick={() => {
                        sendAnalytics(
                          'favorites',
                          showFavourites ? 'Hide Favorites - Builder' : 'View Favorites - Builder',
                          params.pathParams.pathPrefix
                        );
                        setShowFavourites(!showFavourites);
                      }}
                    >
                      {showFavourites ? 'Hide' : 'View'} Saved
                      <br />
                      Designs
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="tw-absolute tw-bottom-1 tw-text-center tw-cursor-pointer tw-left-3 sm:tw-left-6 lg:tw-left-20 tw-flex  xs:tw-flex-col lg:tw-flex-row xs:tw-gap-2 lg:tw-gap-5 secondary-font-family">
              <If condition={myHistory.length}>
                <div className="undo_button" onClick={handleUndo} role="presentation">
                  <img src={undoIcon} alt="undo_icon" className="tw-inline-block tw-w-5" />
                  <div className="tw-underline tw-text-gray-600 tw-text-sm">Undo</div>
                </div>
              </If>
              <If condition={key === 'engraving' && engravingInfo?.selectedOption?.has_inside_reversed_option}>
                <div onClick={handleEngravingReversePattern} role="presentation">
                  <img src={ReverseIcon} alt="undo_icon" className="tw-inline-block tw-w-6" />
                  <div className="tw-underline tw-text-gray-600 tw-text-sm tw-m-0.5">
                    Reverse <br />
                    Pattern
                  </div>
                </div>
              </If>
              <If
                condition={
                  (key === 'logo' || key === 'outsideFeature_1') &&
                  SignetEngravingInfo?.selectedOption?.has_outside_reversed_option
                }
              >
                <div onClick={handleLogoReversePattern} role="presentation">
                  <img src={ReverseIcon} alt="undo_icon" className="tw-inline-block tw-w-6" />
                  <div className="tw-underline tw-text-gray-600 tw-text-sm tw-m-0.5">
                    Reverse <br />
                    Pattern
                  </div>
                </div>
              </If>
            </div>
          </div>
          <div className={clsx(`tw-relative customizer_container tw-flex-1 tw-flex tw-flex-col ${key}`)}>
            <div id="main-content" className={clsx('tw-flex-1', { 'tw-hidden': open })}>
              {showFavourites ? (
                <BuilderFavourites
                  favourites={favourites}
                  currentProductUrl={product?.ring_options_url}
                  onNavigate={handleFavouriteNavigate}
                  onCurrentToggle={() => dispatch(getFavourites())}
                  subpath={params.pathParams.pathPrefix}
                />
              ) : (
                pages.length > 0 && (
                  <AppTabs
                    tabs={pages}
                    tabStyle="single"
                    onChange={handleUpdate}
                    isFetching={isFetching}
                    activeKey={key}
                    onChangeParentActiveKey={setKey}
                    pathPrefix={params.pathParams.pathPrefix}
                    navigateToUrl={navigateToUrl}
                  />
                )
              )}
            </div>
            <If condition={isDoneAtBottom(params.pathParams.pathPrefix || '')}>
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-3 tw-py-4 tw-w-full tw-top-0 tw-bg-gray-50 lg:tw-hidden tw-border-t tw-border-solid tw-border-gray-300">
                <button
                  className="tw-bg-brand-color tw-text-tertiary-color tw-text-base tw-w-3/4 tw-py-2 tw-rounded-tertiary tw-normal-case tw-font-[inherit]"
                  onClick={handleDone}
                >
                  Done{product?.price ? ` - ${product.price}` : ''}
                </button>
              </div>
            </If>
            {!open && (
              <div
                role="presentation"
                className="tw-absolute tw-left-3 tw-top-4 tw-cursor-pointer tw-flex lg:tw-hidden"
                onClick={toggleOpen}
              >
                <i className="fa fa-ellipsis-vertical tw-pr-0.5 tw-text-brand-color" />
                <i className="fa fa-bars tw-text-brand-color" />
                {/* <span className="tw-text-base tw-font-bold tw-ml-3">All Steps</span> */}
              </div>
            )}
            <AppModal size="lg" isOpen={open} onClose={toggleOpen} hideCloseIcon={true}>
              <MobileSidebar
                pages={pages}
                selectedTab={showFavourites ? '' : key}
                handleActiveTab={handleActiveTab}
                toggleOpen={toggleOpen}
                favouritesCount={favoritesEnabled ? favourites.length : 0}
                showFavourites={showFavourites}
                onFavouritesClick={
                  favoritesEnabled
                    ? () => {
                        sendAnalytics('favorites', 'View Favorites - Builder', pathPrefix);
                        setShowFavourites(true);
                        setOpen(false);
                      }
                    : undefined
                }
              />
            </AppModal>
          </div>
        </div>
        {(() => {
          const discountId = params.otherQueryParams?.bestSeller;
          const canShowSimilarRings = Boolean(bProduct?.show_similar_rings && bProduct?.similar_rings?.length > 0);
          const showLoadingPlaceholder = Boolean(
            discountOpen && isBestSellerLoading(bProduct, discountId, bestSellerPendingId)
          );
          const showMagicNumber = Boolean(
            APP_STYLES[params.pathParams.pathPrefix || '']?.showMagicNumberAtDiscountPopup
          );
          return (
            <AppModal
              size="xxl"
              isOpen={discountOpen}
              onClose={() => setDiscountOpen(false)}
              adjustToContent={canShowSimilarRings || showLoadingPlaceholder}
            >
              <DiscountedPopup
                id={discountId}
                handleBestSeller={handleBestSeller}
                engraving={engraving}
                setEngraving={setEngraving}
                handleBestSellerDone={handleBestSellerDone}
                handleCustomize={handleCustomize}
                product={product}
                navigateToUrl={navigateToUrl}
                isOpen={discountOpen}
                showMagicNumber={showMagicNumber}
                initialSize={proceedInfo?.size}
                onClose={() => setDiscountOpen(false)}
              />
            </AppModal>
          );
        })()}
        {/* Ready to Ship confirmation (store-facing) */}
        <AppModal
          size="sm"
          isOpen={proceedInfoOpen}
          onClose={() => {
            applySelectionsToBuilder(getSizeSku(proceedInfo?.size));
            setProceedInfoOpen(false);
          }}
        >
          <div className="tw-p-6">
            <h4 className="tw-font-reckless tw-text-center tw-text-2xl tw-font-bold tw-mb-6">Ready to Ship</h4>
            <div className="tw-flex tw-flex-col">
              <div className="tw-flex tw-justify-between tw-py-3 tw-border-b tw-border-solid tw-border-gray-200">
                <span className="tw-font-medium">Price</span>
                <span>{proceedInfo?.price}</span>
              </div>
              <div className="tw-flex tw-justify-between tw-py-3 tw-border-b tw-border-solid tw-border-gray-200">
                <span className="tw-font-medium">Ring Category Sku</span>
                <span>{proceedInfo?.sku}</span>
              </div>
              <div className="tw-flex tw-justify-between tw-py-3 tw-border-b tw-border-solid tw-border-gray-200">
                <span className="tw-font-medium">Reference Code</span>
                <span>{proceedInfo?.referenceCode}</span>
              </div>
              <div className="tw-flex tw-justify-between tw-py-3 tw-border-b tw-border-solid tw-border-gray-200">
                <span className="tw-font-medium">Size</span>
                <span>{proceedInfo?.size}</span>
              </div>
              {proceedInfo?.engravingText && (
                <>
                  <div className="tw-flex tw-justify-between tw-py-3 tw-border-b tw-border-solid tw-border-gray-200">
                    <span className="tw-font-medium">Engraving Font</span>
                    <span>{proceedInfo.engravingFont}</span>
                  </div>
                  <div className="tw-flex tw-justify-between tw-py-3 tw-border-b tw-border-solid tw-border-gray-200">
                    <span className="tw-font-medium">Engraving</span>
                    <span>{proceedInfo.engravingText}</span>
                  </div>
                </>
              )}
              <div className="tw-flex tw-justify-between tw-gap-6 tw-py-3">
                <span className="tw-font-medium tw-shrink-0">Description</span>
                <span className="tw-text-right">{proceedInfo?.description}</span>
              </div>
            </div>
            <button
              className="hover:tw-bg-brand-color hover:tw-text-tertiary-color tw-block tw-bg-brand-color tw-font-medium tw-px-9 tw-py-2 tw-mt-8 tw-rounded-tertiary tw-text-tertiary-color tw-w-full tw-text-center"
              onClick={() => {
                setProceedInfoOpen(false);
                setDiscountOpen(true);
              }}
            >
              Back to Options
            </button>
          </div>
        </AppModal>
        {/* Screensaver - Fullscreen overlay */}
        {inactivityModalOpen && screensaverData?.active && screensaverData?.video_path && (
          <div className="screensaver-backdrop" />
        )}
        {inactivityModalOpen && screensaverData?.active && screensaverData?.video_path && (
          <div
            className="screensaver-overlay"
            onClick={() => {
              trackScreensaverClick('Screensaver Dismiss');
              closeInactivityModal();
            }}
            role="presentation"
            style={{
              transition: `opacity ${SCREENSAVER_CONFIG.FADE_DURATION}ms ease-in-out`,
              opacity: isFading ? 0 : 1
            }}
          >
            <video
              ref={videoRef}
              src={screensaverVideoUrl}
              autoPlay
              muted
              loop
              playsInline
              onError={(e) => {
                console.error('Screensaver video failed to load:', e);
                // Optionally show a fallback image or message
              }}
              className="screensaver-video"
            />
            {/* Close button */}
            <button
              className="screensaver-close-btn"
              onClick={() => {
                trackScreensaverClick('Close Button');
                closeInactivityModal();
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L17 17M17 1L1 17" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>
            {/* Logo */}
            <img src={LashbrookLogo} alt="Lashbrook" className="screensaver-logo" />
            <div className="screensaver-continue-text">Click anywhere to continue</div>
            {/* Call to action - temporarily hidden
            {SCREENSAVER_RINGS.length > 0 && (
              <div className="screensaver-cta">
                <div>Tap to Design Your Ring</div>
                <div className="screensaver-cta-sub">Or Choose a Ring to Get Started</div>
              </div>
            )}
            */}
            {/* Ring Carousel - temporarily hidden
            {SCREENSAVER_RINGS.length > 0 && (
              <div
                className="screensaver-carousel"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                role="presentation"
              >
                <div
                  className="screensaver-carousel-arrow arrow-up"
                  onClick={() => {
                    trackScreensaverClick('Ring Scroll Up');
                    carouselPausedUntilRef.current = Date.now() + 20000;
                    const totalPages = Math.ceil(SCREENSAVER_RINGS.length / 4);
                    setCarouselPage((prev) => (prev - 1 + totalPages) % totalPages);
                  }}
                  role="button"
                  tabIndex={0}
                />
                <div className="screensaver-carousel-pages">
                  {Array.from({ length: Math.ceil(SCREENSAVER_RINGS.length / 4) }).map((_, pageIndex) => (
                    <div
                      key={pageIndex}
                      className={`screensaver-carousel-page ${pageIndex === carouselPage ? 'active' : ''}`}
                    >
                      {SCREENSAVER_RINGS.slice(pageIndex * 4, pageIndex * 4 + 4).map((ring, i) => {
                        const ringIndex = pageIndex * 4 + i;
                        return (
                          <button
                            key={ringIndex}
                            type="button"
                            className="screensaver-ring-slide"
                            onClick={() => {
                              trackScreensaverClick(ring.ringUrl);
                              closeInactivityModal();
                              setTimeout(() => {
                                history.push(ring.ringUrl);
                              }, SCREENSAVER_CONFIG.FADE_DURATION);
                            }}
                          >
                            <div className="screensaver-ring-info">
                              <span className="screensaver-ring-description">{ring.description}</span>
                              {ringPrices[ringIndex] && (
                                <span className="screensaver-ring-price">{ringPrices[ringIndex]}</span>
                              )}
                            </div>
                            <img src={ring.imageUrl} alt={`Ring ${ringIndex + 1}`} />
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div
                  className="screensaver-carousel-arrow arrow-down"
                  onClick={() => {
                    trackScreensaverClick('Ring Scroll Down');
                    carouselPausedUntilRef.current = Date.now() + 20000;
                    const totalPages = Math.ceil(SCREENSAVER_RINGS.length / 4);
                    setCarouselPage((prev) => (prev + 1) % totalPages);
                  }}
                  role="button"
                  tabIndex={0}
                />
              </div>
            )}
            */}
          </div>
        )}
        <Loading timedOut={isFetching} />
      </div>
    </>
  );
};

const RingCustomizerContainer: FC<ICustomizerHOCProps> = RingCustomizerHOC(RingCustomizer);

export default RingCustomizerContainer;
